import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import InfoTooltip from "../components/common/InfoTooltip";
import { useAuth } from "../context/AuthContext";
import { getBillingStatement } from "../api/billing";
import { formatInTimeZone } from "../utils/datetime";

const MODAL_TEXT = "text-sm md:text-base";

const formatCurrencyFromCents = (amountCents, currency = "cad") => {
  const value = Number(amountCents || 0) / 100;
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: (currency || "cad").toUpperCase(),
  }).format(value);
};

const formatDateInputInTimeZone = (dateValue, timeZone) => {
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(dateValue);
  } catch (err) {
    return new Intl.DateTimeFormat("en-CA", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(dateValue);
  }
};

const formatTransactionLabel = (row) => {
  if (row?.type === "payment") return "Payment";
  if (row?.invoice_number) return `Invoice #${row.invoice_number}`;
  if (row?.stripe_invoice_id) return `Invoice ${row.stripe_invoice_id}`;
  return "Adjustment";
};

const formatStatementDateTime = (value, timeZone) =>
  formatInTimeZone(
    value,
    timeZone,
    {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    },
    "en-CA"
  );

export default function BillingStatements() {
  const { token, user } = useAuth();
  const navigate = useNavigate();

  const fallbackTimeZone = user?.salon_timezone || "America/Toronto";
  const todayInTz = formatDateInputInTimeZone(new Date(), fallbackTimeZone);
  const ninetyDaysAgoInTz = formatDateInputInTimeZone(
    new Date(Date.now() - 89 * 24 * 60 * 60 * 1000),
    fallbackTimeZone
  );

  const [fromDate, setFromDate] = useState(ninetyDaysAgoInTz);
  const [toDate, setToDate] = useState(todayInTz);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [statement, setStatement] = useState(null);

  const timeZone = useMemo(
    () => statement?.salon_timezone || fallbackTimeZone,
    [statement?.salon_timezone, fallbackTimeZone]
  );

  const applyQuickRange = (days) => {
    const to = formatDateInputInTimeZone(new Date(), timeZone);
    const from = formatDateInputInTimeZone(
      new Date(Date.now() - Math.max(0, days - 1) * 24 * 60 * 60 * 1000),
      timeZone
    );
    setFromDate(from);
    setToDate(to);
  };

  const loadStatement = async () => {
    if (!fromDate || !toDate) {
      setError("Select both from and to dates.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const data = await getBillingStatement(token, {
        from: fromDate,
        to: toDate,
      });
      setStatement(data);
    } catch (err) {
      setStatement(null);
      setError(err?.response?.data?.detail || "Unable to load statements.");
    } finally {
      setLoading(false);
    }
  };

  const printableRows = Array.isArray(statement?.transactions)
    ? statement.transactions
    : [];

  return (
    <div className="manageSectionShell">
      <style>
        {`@media print {
          .no-print { display: none !important; }
          .print-surface { border: 0 !important; box-shadow: none !important; border-radius: 0 !important; }
          .print-pad { padding: 0 !important; }
          body { background: white !important; }
        }`}
      </style>
      <div
        className={`manageSectionWide manage-section-wide manage-section-compact ${MODAL_TEXT}`}
      >
        <div className="print-surface rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <div className="no-print bg-rose-500 px-6 py-4">
            <div className="flex items-center justify-between gap-3">
              <h1 className="text-xl md:text-2xl font-semibold text-white">Statements</h1>
              <button
                type="button"
                onClick={() => navigate(-1)}
                aria-label="Close and go back"
                className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-white/45 bg-white/10 text-xl font-semibold leading-none text-white transition hover:bg-white/20"
              >
                X
              </button>
            </div>
          </div>

          <div className="print-pad px-6 py-6 space-y-6">
            {error && (
              <div className="no-print rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            )}

            <div className="no-print rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 lg:p-6 space-y-4">
              <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <div>
                  <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                    Statement Range
                  </h2>
                  <p className="text-sm text-slate-500">
                    Choose a date range and generate a printable statement.
                  </p>
                </div>
                <div className="text-xs text-slate-500">{timeZone}</div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                <label className="space-y-1">
                  <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    From
                    <InfoTooltip text="Start date in salon timezone." label="From" />
                  </span>
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                  />
                </label>
                <label className="space-y-1">
                  <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    To
                    <InfoTooltip text="End date in salon timezone." label="To" />
                  </span>
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                  />
                </label>
                <div className="md:col-span-2 flex flex-wrap items-end gap-2">
                  <button
                    type="button"
                    onClick={() => applyQuickRange(90)}
                    className="rounded-full border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Last 90 days
                  </button>
                  <button
                    type="button"
                    onClick={() => applyQuickRange(180)}
                    className="rounded-full border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Last 180 days
                  </button>
                  <button
                    type="button"
                    onClick={() => applyQuickRange(365)}
                    className="rounded-full border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Last 365 days
                  </button>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => navigate("/manager/manage/billing")}
                  className="rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={loadStatement}
                  disabled={loading}
                  className="rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
                >
                  {loading ? "Loading..." : "Go"}
                </button>
                <button
                  type="button"
                  onClick={() => window.print()}
                  disabled={!statement}
                  className="rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                >
                  Print
                </button>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 lg:p-6 space-y-4">
              <div>
                <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                  {statement?.statement_label || `${fromDate} to ${toDate}`}
                </h2>
                <p className="text-sm text-slate-500">Timezone: {timeZone}</p>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
                <table className="w-full table-fixed text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-xs font-semibold uppercase tracking-wide">
                    <tr>
                      <th className="px-3 py-2 text-left w-56">Date</th>
                      <th className="px-3 py-2 text-left">Transaction</th>
                      <th className="px-3 py-2 text-right w-44">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {!statement && (
                      <tr>
                        <td colSpan={3} className="px-3 py-4 text-slate-500">
                          Run a statement range to view transactions.
                        </td>
                      </tr>
                    )}
                    {statement && printableRows.length === 0 && (
                      <tr>
                        <td colSpan={3} className="px-3 py-4 text-slate-500">
                          No transactions in this range.
                        </td>
                      </tr>
                    )}
                    {statement &&
                      printableRows.map((row) => {
                        const amount = Number(row?.amount_cents || 0);
                        return (
                          <tr key={row.id}>
                            <td className="px-3 py-2.5 text-slate-700 whitespace-nowrap">
                              {formatStatementDateTime(row.occurred_at_utc, timeZone)}
                            </td>
                            <td className="px-3 py-2.5 text-slate-700">
                              {formatTransactionLabel(row)}
                            </td>
                            <td className="px-3 py-2.5 text-right font-semibold text-slate-900">
                              {formatCurrencyFromCents(amount, row?.currency)}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
